import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys, type AuditFilters } from "@/api/queryKeys";
import type { DataResponse, ListResponse } from "@/api/schemas";

/**
 * História zmien — read-only nad `audit_log`.
 *
 * Typy sú lokálne, `types.gen.ts` ich zatiaľ neobsahuje (regenerujú sa
 * cez `npm run gen:types` po ďalšom OpenAPI sync-u) — rovnako ako
 * `useAuth.ts`.
 */

export interface AuditPoleZmena {
  pole: string;
  pred: unknown;
  po: unknown;
}

export interface AuditZaznam {
  id: number;
  tabulka: string;
  record_id: string | null;
  action: string;
  autor: string | null;
  created_at: string;
  zmeny: AuditPoleZmena[];
  popis: string | null;
  /** Komu záznam patrí — z `tabulka` + `record_id` sa to odvodiť nedá. */
  zakazka_id: string | null;
  zakazka_cislo: string | null;
  zostava_id: string | null;
  zostava_kod: string | null;
}

export interface AuditFiltreData {
  autori: string[];
  tabulky: string[];
}

/** Vyčistí prázdne filtre — inak by šlo `?autor=` a nič nevrátilo. */
function paramy(filters: AuditFilters): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") out[k] = String(v);
  }
  return out;
}

export function useAudit(
  filters: AuditFilters,
  enabled = true,
): UseQueryResult<ListResponse<AuditZaznam>> {
  return useQuery({
    queryKey: queryKeys.audit.list(filters),
    queryFn: async () => {
      const { data } = await api.get<ListResponse<AuditZaznam>>("/audit", {
        params: paramy(filters),
      });
      return data;
    },
    enabled,
  });
}

/** Hodnoty do selectov — kto v histórii vystupuje a ktoré tabuľky. */
export function useAuditFiltre(
  enabled = true,
): UseQueryResult<DataResponse<AuditFiltreData>> {
  return useQuery({
    queryKey: queryKeys.audit.filtre(),
    queryFn: async () => {
      const { data } =
        await api.get<DataResponse<AuditFiltreData>>("/audit/filtre");
      return data;
    },
    staleTime: 60_000,
    enabled,
  });
}
